import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { DateFormatPipe } from '../../../../_shared/pipes/date-formatter.pipe';
import { FilterService } from '../../../../_shared/services';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { FuelLevel } from '../../models/fuel-level.model';
import { FuelLevelService } from '../../services/fuel-level.service';
import { FuelLevelDisplayComponent } from '../fuel-level-display/fuel-level-display.component';
import { CommonModule } from '@angular/common';
import { FuelLevelStateService } from '../../services/fuel-level-state.service';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { ErrorStateComponent } from '../../../../_shared/components/states/error-state/error-state.component';
import { EmptyStateComponent } from '../../../../_shared/components/states/empty-state/empty-state.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-levels',
  standalone: true,
  imports: [
    DateFormatPipe,
    FuelLevelDisplayComponent,
    CommonModule,
    ScrollingModule,
    NgbTooltipModule,
    EmptyStateComponent,
    ErrorStateComponent,
    TranslocoDirective
  ],
  templateUrl: './fuel-levels.component.html',
  styleUrl: './fuel-levels.component.scss'
})
export class FuelLevelsComponent implements OnInit, OnDestroy {
  @ViewChild('tableContainer') tableContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  private fuelLevelService: FuelLevelService = inject(FuelLevelService);
  private filterService: FilterService = inject(FilterService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private stateService: FuelLevelStateService = inject(FuelLevelStateService);
  private transloco: TranslocoService = inject(TranslocoService);

  public isLoading: boolean = true;
  public hasError: boolean = false;
  public fuelLevels: FuelLevel[] = [];
  public filteredFuelLevels: FuelLevel[] = [];

  private cancelSubject: Subject<void> = new Subject<void>();
  private destroySubject: Subject<void> = new Subject<void>();
  selectedFuelLevelId: number;

  ngOnInit(): void {
    this.stateService.setFuelLevels([]);
    this.stateService.setIsLoading(this.isLoading);

    this.filterService.filterUpdated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((filter) => {
        this.loadFuelLevels(filter.accountIds);
      });

    this.stateService.lowFuelLevelSelected$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((fuelLevel) => {
        this.scrollToItem(fuelLevel.id);
        this.selectFuelLevel(fuelLevel);
      });

    this.filterService.searchTerm$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((searchTerm) => {
        if (searchTerm) {
          this.filteredFuelLevels = [...this.fuelLevels.filter(x => x.assetName.toLowerCase().includes(searchTerm.toLowerCase()))];
        } else {
          this.filteredFuelLevels = [...this.fuelLevels];
        }
      });
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  async loadFuelLevels(accountIds: number[]): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;
      this.cancelSubject.next();
      const response = await this.fuelLevelService.getFuelLevelsAsync(accountIds, this.cancelSubject);
      this.fuelLevels = response.data ?? [];
      this.filteredFuelLevels = [...this.fuelLevels];
      this.stateService.setFuelLevels(this.fuelLevels);

      this.isLoading = false;
      this.stateService.setIsLoading(this.isLoading);
    } catch (ex) {
      this.errorHandlerService.handleError(ex, {
        callback: () => {
          this.isLoading = false;
          this.hasError = true;
          this.stateService.setIsLoading(this.isLoading);
        }
      });
    }
  }

  selectFuelLevel(fuelLevel: FuelLevel): void {
    if (this.selectedFuelLevelId === fuelLevel.id) {
      this.selectedFuelLevelId = 0;
      this.stateService.setFuelLevels(this.fuelLevels);
    } else {
      this.selectedFuelLevelId = fuelLevel.id;
      this.stateService.setFuelLevels([fuelLevel]);
    }
  }

  scrollToItem(itemId: number): void {
    const index = this.fuelLevels.findIndex(item => item.id === itemId);
    if (index !== -1) {
      this.viewport.scrollToIndex(index, 'smooth');
    }
  }

  getDistanceToEmpty(distanceToEmpty: number): string {
    return distanceToEmpty ? `${distanceToEmpty}km` : this.transloco.translate('fuelLevel.common.notAvailable')
  }
}